import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { useFlowium } from '../client/FlowiumContext';

import AuthLayout from '../layout/AuthLayout';
import Button from '../components/Button';
import Input from '../components/Input';
import { GOOGLE_AUTH_CLIENT_ID } from '../const';
import { EnvelopeIcon, LockIcon } from '../components/Icons';
import { getErrorMessage } from '../utils';

const Register: React.FC = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { client } = useFlowium();

    const isValidEmail = (email: string) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleRegister = async () => {
        if (!email || !password) {
            setError('Both fields are required');
            return;
        }
        if (!isValidEmail(email)) {
            setError('Please enter a valid email address');
            return;
        }
        setLoading(true);
        try {
            const response = await client.register(email, password);
            setMessage(response.message);
            setError(''); // clear any previous error messages
        } catch (error: unknown) {
            const message = getErrorMessage(error);
            if (message.includes('already registered with Google login')) {
                setError('This email is already registered with Google. Please use the "Continue with Google" button below or try a different email.');
            } else if (message.includes('already exists')) {
                setError('This email is already registered. Please try logging in instead.');
            } else {
                setError('Error registering user');
            }
            setMessage(''); // clear any previous success messages
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleRegister = async (credentialResponse: { credential?: string }) => {
        try {
            await client.googleLogin(credentialResponse.credential ?? '');
            setMessage('User registered successfully via Google. Please check your email to verify your account.');
            setError('');
        } catch (error: unknown) {
            const message = getErrorMessage(error);
            if (message.includes('already registered with email/password')) {
                setError('This email is already registered with email/password. Please use the form above or try a different email.');
            } else {
                setError('Google registration failed');
            }
            setMessage('');
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && email && password) {
            handleRegister();
        }
    };

    return (
        <GoogleOAuthProvider clientId={GOOGLE_AUTH_CLIENT_ID}>
            <AuthLayout title="Create Your Account">
                <Input
                    label="Email"
                    placeholder="xavier@xmen.com"
                    icon={<EnvelopeIcon />}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading || Boolean(message)}
                    className="mb-4 mt-6"
                />
                <Input
                    label="Password"
                    type="password"
                    placeholder="********"
                    icon={<LockIcon />}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={loading || Boolean(message)}
                    className="mb-1"
                />

                <Button
                    onClick={handleRegister}
                    disabled={loading || !email || !password || Boolean(message)}
                    className='w-full mt-8'
                >
                    {loading ? 'Registering...' : 'Register'}
                </Button>
                {error && <p className="text-error mt-4 text-sm">{error}</p>}
                {message && <p className="text-success mt-4 text-sm">{message}</p>}

                {message ? (
                    <p className="mt-4 text-center">
                        <Link to="/login" className="text-content hover:underline">Go to Login</Link>
                    </p>
                ) : (
                    <p className="mt-4 text-center text-sm font-light">
                        <span className="text-content-secondary">Already have an account?</span>
                        <button onClick={() => navigate('/login')} className="ml-1 text-accent hover:underline">
                            Login
                        </button>
                    </p>
                )}

                <div className="mt-8 text-center">
                    <div className="text-content-tertiary text-xs mb-4">
                        — or continue with —
                    </div>
                    <GoogleLogin
                        onSuccess={handleGoogleRegister}
                        onError={() => {
                            setError('Google registration failed');
                        }}
                        useOneTap
                        width="100%"
                    />
                </div>
                <div className="mt-6 text-center">
                    <p className="text-xs text-content-tertiary">
                        <strong>Note:</strong> You can only use one authentication method per email address. Choose carefully!
                    </p>
                </div>
            </AuthLayout>
        </GoogleOAuthProvider>
    );
};

export default Register;
